import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CreditCard, Banknote, ShieldCheck, Tag, X, Plus, Home as HomeIcon } from "lucide-react";

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, refresh } = useCart();
  const { user, loading: authLoading } = useAuth();
  const [address, setAddress] = useState({
    full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: ""
  });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState("new");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [razorEnabled, setRazorEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [validating, setValidating] = useState(false);

  const shipping = subtotal >= 999 ? 0 : 79;
  const discount = coupon?.discount || 0;
  const total = Math.max(0, subtotal + shipping - discount);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    api.get("/payments/razorpay/config").then((r) => setRazorEnabled(r.data.enabled));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/addresses").then((r) => {
      setSavedAddresses(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) {
        setSelectedAddrId(def.id);
        setAddress({ full_name: def.full_name, phone: def.phone, line1: def.line1, line2: def.line2 || "", city: def.city, state: def.state, pincode: def.pincode });
      }
    });
    setAddress((a) => ({ ...a, full_name: a.full_name || user.name }));
  }, [user]);

  const selectAddress = (id) => {
    setSelectedAddrId(id);
    if (id === "new") {
      setAddress({ full_name: user?.name || "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
    } else {
      const a = savedAddresses.find((x) => x.id === id);
      if (a) setAddress({ full_name: a.full_name, phone: a.phone, line1: a.line1, line2: a.line2 || "", city: a.city, state: a.state, pincode: a.pincode });
    }
  };

  const onChange = (k, v) => setAddress({ ...address, [k]: v });

  const validateAddress = () => {
    if (!address.full_name.trim()) return "Name required";
    if (!/^\d{10}$/.test(address.phone)) return "Valid 10-digit phone required";
    if (!address.line1.trim()) return "Address required";
    if (!address.city.trim()) return "City required";
    if (!address.state.trim()) return "State required";
    if (!/^\d{6}$/.test(address.pincode)) return "Valid 6-digit pincode required";
    return null;
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidating(true);
    try {
      const res = await api.post("/coupons/validate", { code: couponCode.trim().toUpperCase(), subtotal });
      setCoupon(res.data);
      toast.success(`Coupon ${res.data.code} applied: -₹${res.data.discount}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid coupon");
      setCoupon(null);
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => { setCoupon(null); setCouponCode(""); };

  const placeOrderCOD = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/orders", { address, payment_method: "cod", coupon_code: coupon?.code });
      toast.success("Order placed!");
      await refresh();
      navigate(`/orders/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const placeOrderRazorpay = async () => {
    setSubmitting(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay");
      const orderRes = await api.post("/payments/razorpay/order", coupon ? { code: coupon.code, subtotal } : null);
      const { order_id, amount, currency, key_id } = orderRes.data;
      const options = {
        key: key_id, amount, currency, order_id,
        name: "Sutrakosh by Chanchala", description: "Handmade with love",
        prefill: { name: user.name, email: user.email, contact: address.phone },
        theme: { color: "#D81B60" },
        handler: async (resp) => {
          try {
            const orderResult = await api.post("/orders", {
              address, payment_method: "razorpay",
              coupon_code: coupon?.code,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful");
            await refresh();
            navigate(`/orders/${orderResult.data.id}`);
          } catch (e) {
            toast.error("Payment verified but order failed");
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Razorpay error");
      setSubmitting(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const err = validateAddress();
    if (err) return toast.error(err);
    if (items.length === 0) return toast.error("Cart is empty");
    if (paymentMethod === "cod") placeOrderCOD();
    else placeOrderRazorpay();
  };

  if (authLoading) return <div className="text-center py-20 font-display text-2xl text-deepbrown/60">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="checkout-page">
      <h1 className="font-display text-4xl sm:text-5xl text-deepbrown font-bold">Checkout</h1>
      <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">

          {/* Saved addresses */}
          {savedAddresses.length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-deepbrown/10">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-display text-2xl text-deepbrown">Choose Address</h2>
                <Link to="/addresses" className="text-sm text-rani font-bold hover:underline">Manage</Link>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {savedAddresses.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => selectAddress(a.id)}
                    className={`text-left p-4 rounded-xl border-2 transition ${selectedAddrId === a.id ? "border-rani bg-rani/5" : "border-deepbrown/10 hover:border-rani/40"}`}
                    data-testid={`select-address-${a.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <HomeIcon className="h-4 w-4 text-rani" />
                      <span className="font-bold text-deepbrown">{a.label}</span>
                      {a.is_default && <span className="text-[9px] uppercase tracking-widest font-bold bg-emerald text-white px-1.5 py-0.5 rounded-full">Default</span>}
                    </div>
                    <p className="text-xs text-deepbrown/70 mt-2 leading-relaxed">{a.full_name} · {a.line1}, {a.city} {a.pincode}</p>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => selectAddress("new")}
                  className={`text-left p-4 rounded-xl border-2 border-dashed transition flex items-center gap-2 ${selectedAddrId === "new" ? "border-rani bg-rani/5 text-rani" : "border-deepbrown/20 text-deepbrown/60 hover:border-rani/40"}`}
                  data-testid="select-new-address"
                >
                  <Plus className="h-4 w-4" />
                  <span className="font-bold">Use a new address</span>
                </button>
              </div>
            </div>
          )}

          {/* Address form */}
          <div className="bg-white rounded-2xl p-6 border border-deepbrown/10">
            <h2 className="font-display text-2xl text-deepbrown mb-4">{savedAddresses.length === 0 ? "Shipping Address" : "Delivery Details"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full name</Label>
                <Input value={address.full_name} onChange={(e) => onChange("full_name", e.target.value)} data-testid="addr-name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={address.phone} onChange={(e) => onChange("phone", e.target.value)} maxLength={10} data-testid="addr-phone" />
              </div>
              <div className="md:col-span-2">
                <Label>Address line 1</Label>
                <Input value={address.line1} onChange={(e) => onChange("line1", e.target.value)} data-testid="addr-line1" />
              </div>
              <div className="md:col-span-2">
                <Label>Address line 2 (optional)</Label>
                <Input value={address.line2} onChange={(e) => onChange("line2", e.target.value)} data-testid="addr-line2" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={address.city} onChange={(e) => onChange("city", e.target.value)} data-testid="addr-city" />
              </div>
              <div>
                <Label>State</Label>
                <Input value={address.state} onChange={(e) => onChange("state", e.target.value)} data-testid="addr-state" />
              </div>
              <div>
                <Label>Pincode</Label>
                <Input value={address.pincode} onChange={(e) => onChange("pincode", e.target.value)} maxLength={6} data-testid="addr-pincode" />
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-2xl p-6 border border-deepbrown/10">
            <h2 className="font-display text-2xl text-deepbrown mb-4">Payment Method</h2>
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
              <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${paymentMethod === "cod" ? "border-rani bg-rani/5" : "border-deepbrown/10"}`}>
                <RadioGroupItem value="cod" data-testid="pay-cod" />
                <Banknote className="h-6 w-6 text-emerald" />
                <div>
                  <p className="font-bold text-deepbrown">Cash on Delivery</p>
                  <p className="text-xs text-deepbrown/60">Pay when your order arrives</p>
                </div>
              </label>
              <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${paymentMethod === "razorpay" ? "border-rani bg-rani/5" : "border-deepbrown/10"} ${!razorEnabled ? "opacity-50" : ""}`}>
                <RadioGroupItem value="razorpay" data-testid="pay-razorpay" disabled={!razorEnabled} />
                <CreditCard className="h-6 w-6 text-rani" />
                <div className="flex-1">
                  <p className="font-bold text-deepbrown">Razorpay – Cards · UPI · Netbanking</p>
                  <p className="text-xs text-deepbrown/60">
                    {razorEnabled ? "Secure payment via Razorpay" : "Razorpay not configured yet — admin needs to add keys"}
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-4">
          <div className="bg-white rounded-2xl p-6 border border-deepbrown/10 sticky top-24">
            <h3 className="font-display text-2xl text-deepbrown mb-4">Order Summary</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
              {items.map((it) => (
                <div key={it.id} className="flex gap-3 text-sm">
                  <img src={it.product.images[0]} className="h-12 w-12 rounded-lg object-cover" alt="" />
                  <div className="flex-1">
                    <p className="font-semibold line-clamp-1 text-deepbrown">{it.product.name}</p>
                    <p className="text-xs text-deepbrown/60">Qty {it.quantity}</p>
                  </div>
                  <p className="font-bold text-deepbrown">₹{(it.product.price * it.quantity).toLocaleString("en-IN")}</p>
                </div>
              ))}
            </div>

            {/* Coupon */}
            <div className="mt-4 pt-4 border-t border-deepbrown/10">
              {!coupon ? (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-deepbrown/40" />
                    <Input
                      placeholder="Promo code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="pl-9"
                      data-testid="coupon-input"
                    />
                  </div>
                  <button type="button" onClick={applyCoupon} disabled={validating} className="px-4 rounded-md bg-deepbrown text-ivory text-sm font-bold hover:bg-rani disabled:opacity-50" data-testid="apply-coupon-btn">
                    Apply
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-emerald/10 border border-emerald/30 rounded-xl p-3">
                  <div>
                    <p className="font-bold text-emerald text-sm">{coupon.code}</p>
                    <p className="text-[11px] text-emerald/80">−₹{coupon.discount} off</p>
                  </div>
                  <button type="button" onClick={removeCoupon} className="text-emerald hover:text-rani" data-testid="remove-coupon-btn">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              <p className="text-[11px] text-deepbrown/50 mt-2">Try <b>WELCOME10</b> or <b>DIWALI100</b></p>
            </div>

            <div className="mt-4 pt-4 border-t border-deepbrown/10 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? <span className="text-emerald">FREE</span> : `₹${shipping}`}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald"><span>Discount ({coupon.code})</span><span>−₹{discount.toLocaleString("en-IN")}</span></div>
              )}
              <div className="flex justify-between font-display text-xl pt-2 border-t border-deepbrown/10">
                <span>Total</span>
                <span className="text-rani font-bold">₹{total.toLocaleString("en-IN")}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 py-4 rounded-full bg-rani text-white font-bold shadow-lg hover:scale-[1.02] transition-transform disabled:opacity-50"
              data-testid="place-order-btn"
            >
              {submitting ? "Placing…" : `Place Order · ₹${total.toLocaleString("en-IN")}`}
            </button>
            <p className="text-[10px] text-deepbrown/50 mt-3 flex items-center justify-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Your data is encrypted and secure
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
